import { useState } from 'react';
import { FileCode, ImageSquare, LinkSimple, PlusSquare, Trash } from '@phosphor-icons/react';
import { useToast } from '@/hooks/use-toast';
import { copyTextToClipboard } from '@/lib/clipboard';
import type { MediaAsset } from '../types/media-library.types';
import { formatAssetDate, formatFileSize } from '../utils/media-library.utils';

type MediaCardProps = {
  asset: MediaAsset;
  onInsert: (asset: MediaAsset) => void;
  onDelete: (asset: MediaAsset) => void;
};

export const MediaCard = ({ asset, onInsert, onDelete }: MediaCardProps) => {
  const { toast } = useToast();
  const [imageError, setImageError] = useState(false);
  const [dimensions, setDimensions] = useState<string | null>(
    asset.width && asset.height ? `${asset.width} × ${asset.height}` : null
  );

  const formattedDate = formatAssetDate(asset.createdAt || asset.createdDate);
  const formattedSize = formatFileSize(asset.sizeInBytes);
  const isSvg = asset.type.toUpperCase() === 'SVG';

  const handleCopyLink = async () => {
    if (!asset.url) {
      toast({
        variant: 'destructive',
        title: 'No URL available',
        description: 'This asset does not have a public URL to copy.',
      });
      return;
    }

    try {
      await copyTextToClipboard(asset.url);
      toast({
        variant: 'success',
        title: 'URL copied.',
      });
    } catch {
      toast({
        variant: 'destructive',
        title: 'Failed to copy',
        description: `Could not copy automatically — the URL is ${asset.url}`,
      });
    }
  };

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    if (img.naturalWidth && img.naturalHeight) {
      setDimensions(`${img.naturalWidth} × ${img.naturalHeight}`);
    }
  };

  const showPreview = asset.url && !imageError;

  return (
    <div
      className="group flex flex-col overflow-hidden rounded-[12px] border border-[#E7E8EA] bg-white transition hover:shadow-xs"
      data-id={asset.id}
    >
      {/* Thumbnail area with 16:10 aspect ratio */}
      <div className="relative flex aspect-[16/10] w-full items-center justify-center overflow-hidden bg-[#F4F5F6]">
        {showPreview ? (
          <img
            src={asset.url}
            alt=""
            loading="lazy"
            onLoad={handleImageLoad}
            onError={() => setImageError(true)}
            className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
          />
        ) : (
          <div className="flex flex-col items-center justify-center gap-1.5 p-4 text-center text-[#707070]">
            {isSvg ? (
              <FileCode className="h-7 w-7 text-[#707070]" weight="regular" />
            ) : (
              <ImageSquare className="h-7 w-7 text-[#707070]" weight="regular" />
            )}
            <span className="text-[12px] text-[#707070]">Preview unavailable</span>
          </div>
        )}
      </div>

      {/* Metadata & Actions */}
      <div className="flex flex-1 flex-col gap-1.5 p-3">
        <div
          className="truncate text-[14px] font-medium leading-5 text-[#1B2021]"
          title={asset.name}
        >
          {asset.name}
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          <span className="inline-flex items-center rounded-full bg-[#F4F5F6] px-2 py-0.5 text-[12px] font-medium text-[#27272C]">
            {asset.type}
          </span>
          <span className="text-[12px] text-[#707070]">{formattedSize}</span>
        </div>

        <div className="text-[12px] text-[#707070]">
          {dimensions ? `${dimensions} · ` : ''}
          {formattedDate}
        </div>

        {/* Row icon buttons footer matching prototype */}
        <div className="mt-auto flex items-center gap-1 pt-1">
          <button
            type="button"
            onClick={handleCopyLink}
            className="inline-flex h-8 w-8 flex-none items-center justify-center rounded-[6px] text-[#666666] transition-colors hover:bg-[#F4F5F6] hover:text-[#1B2021]"
            aria-label="Copy URL"
            title="Copy URL"
          >
            <LinkSimple className="h-4 w-4" />
          </button>

          <button
            type="button"
            onClick={() => onInsert(asset)}
            className="inline-flex h-8 w-8 flex-none items-center justify-center rounded-[6px] text-[#666666] transition-colors hover:bg-[#F4F5F6] hover:text-[#1B2021]"
            aria-label="Insert"
            title="Insert snippet"
          >
            <PlusSquare className="h-4 w-4" />
          </button>

          <div className="flex-1" />

          <button
            type="button"
            onClick={() => onDelete(asset)}
            className="inline-flex h-8 w-8 flex-none items-center justify-center rounded-[6px] text-[#666666] transition-colors hover:bg-[#FCE4EA] hover:text-[#D80032]"
            aria-label="Delete"
            title="Delete"
          >
            <Trash className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
